// src/components/MapModal.tsx
import FleetModal from '@/libs/antd/components/Modal';
import FleetTitle from '@/libs/antd/components/Title';
import { FC, useRef, useState, useCallback, useEffect } from 'react';
import { GoogleMap } from '@react-google-maps/api';
import { Form, Image, Input, Typography, Spin } from 'antd';
import { usePlacesAutocompleteNew } from '@/hooks/usePlacesAutocompleteNew';
import { getGeocode, getLatLng } from 'use-places-autocomplete';
import { useApp } from '@/App';
import mapMarker from '@/assets/images/mapmarker.png';

export type MapCenter = {
	address: string;
	center: {
		lat: number;
		lng: number;
	};
};

export type MapModalState = null | {
	title?: string;
	subtitle?: string;
	address?: string;
	latitude?: number;
	longitude?: number;
	onChange?: (e: MapCenter) => void;
	draggable?: boolean;
	zoom?: number;
	width?: string | number;
	height?: string | number;
	viewOnly?: boolean;
};

export type MapModalProps = {
	mapData: MapModalState;
	onClose: () => void;
};

const DEFAULT_LATITUDE = -6.1975577;
const DEFAULT_LONGITUDE = 106.8246861;

const MapModal: FC<MapModalProps> = ({ mapData, onClose }) => {
	const {
		title = 'Lokasi',
		subtitle = 'Geser map dibawah untuk menentukan lokasi yang tepat.',
		address = '',
		latitude = mapData?.latitude ?? DEFAULT_LATITUDE,
		longitude = mapData?.longitude ?? DEFAULT_LONGITUDE,
		onChange = () => false,
		zoom = 17,
		width = '100%',
		height = 540,
		viewOnly = false,
	} = mapData ?? {};

	const { notification } = useApp();
	const [lat, setLat] = useState<number>(latitude);
	const [lng, setLng] = useState<number>(longitude);
	const [defaultAddress, setDefaultAddress] = useState(address);

	useEffect(() => {
		setLat(latitude);
		setLng(longitude);
	}, [latitude, longitude]);

	const center = { lat, lng };

	const { value, setValue, suggestions, clearSuggestions, ready, loading } =
		usePlacesAutocompleteNew({
			requestOptions: {},
			debounce: 300,
			defaultValue: address,
		});

	const handleOk = () => {
		onChange({ address: defaultAddress, center });
		handleClose();
	};

	const handleClose = () => {
		// setValue('');
		// setDefaultAddress('');
		// setLat(DEFAULT_LATITUDE);
		// setLng(DEFAULT_LONGITUDE);
		onClose?.();
	};

	const handleSelect = async (address: string) => {
		setValue(address, false);
		clearSuggestions();

		try {
			const results = await getGeocode({ address });
			const { lat, lng } = await getLatLng(results[0]);
			setLat(lat);
			setLng(lng);
			setDefaultAddress(address);
			if (mapRef.current) {
				mapRef.current.panTo({ lat, lng });
			}
		} catch (error) {
			notification.error({
				message: 'Error',
				description: 'Terjadi kesalahan saat mencari alamat.',
			});
			console.log('Errors: ', error);
		}
	};

	const reverseGeocode = (lat: number, lng: number): Promise<string> => {
		return new Promise((resolve, reject) => {
			const geocoder = new google.maps.Geocoder();
			const latLng = new google.maps.LatLng(lat, lng);

			geocoder.geocode({ location: latLng }, (results, status) => {
				if (status === google.maps.GeocoderStatus.OK && results && results[0]) {
					resolve(results[0].formatted_address);
				} else {
					reject(new Error('Failed to reverse geocode'));
				}
			});
		});
	};

	const mapRef = useRef<google.maps.Map | null>(null);

	const onMapLoad = useCallback((map: google.maps.Map) => {
		mapRef.current = map;
	}, []);

	const onDragEnd = async () => {
		if (mapRef.current) {
			const newCenter = mapRef.current.getCenter();
			if (newCenter) {
				const lat = newCenter.lat();
				const lng = newCenter.lng();
				setLat(lat);
				setLng(lng);
				try {
					const address = await reverseGeocode(lat, lng);
					setDefaultAddress(address);
					setValue(address, false);
				} catch {
					notification.error({
						message: 'Error',
						description: 'Terjadi kesalahan saat mencari alamat. coba lagi',
					});
				}
			}
		}
	};

	return (
		<FleetModal
			open={Boolean(mapData)}
			closable={false}
			maskClosable={false}
			width={940}
			cancelText="BACK"
			buttonPosition="end"
			okText={'SAVE'}
			onOk={handleOk}
			okButtonProps={{ hidden: viewOnly }}
			onCancel={handleClose}
		>
			<FleetTitle>{title}</FleetTitle>
			{viewOnly ? (
				<Form.Item
					labelAlign="left"
					label="Address"
					colon={false}
					style={{ marginBottom: 20 }}
				>
					<Input.TextArea
						value={address}
						disabled
						autoSize={{ minRows: 2, maxRows: 5 }}
					/>
				</Form.Item>
			) : (
				<Form.Item
					labelAlign="left"
					label="Address"
					colon={false}
					style={{ marginBottom: 20 }}
				>
					<div style={{ position: 'relative' }}>
						<Input
							value={value}
							placeholder="Search for a place..."
							onChange={(e) => setValue(e.target.value)}
							suffix={loading ? <Spin size="small" /> : null}
							disabled={!ready}
						/>
						{suggestions.length > 0 && (
							<div
								style={{
									position: 'absolute',
									top: '100%',
									left: 0,
									right: 0,
									background: 'white',
									border: '1px solid #d9d9d9',
									borderRadius: '6px',
									boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
									zIndex: 1000,
									maxHeight: '200px',
									overflowY: 'auto',
								}}
							>
								{suggestions.map((suggestion, index) => (
									<div
										key={suggestion.placeId || index}
										style={{
											padding: '8px 12px',
											cursor: 'pointer',
											borderBottom:
												index < suggestions.length - 1
													? '1px solid #f0f0f0'
													: 'none',
										}}
										onClick={() => {
											handleSelect(suggestion.description);
										}}
										onMouseEnter={(e) => {
											e.currentTarget.style.backgroundColor = '#f5f5f5';
										}}
										onMouseLeave={(e) => {
											e.currentTarget.style.backgroundColor = 'white';
										}}
									>
										{suggestion.description}
									</div>
								))}
							</div>
						)}
					</div>
				</Form.Item>
			)}
			{!viewOnly && (
				<Typography.Paragraph style={{ color: '#1557BC' }}>
					{subtitle}
				</Typography.Paragraph>
			)}
			<div style={{ width, height, position: 'relative' }}>
				<div className="map-modal-marker">
					<Image
						preview={false}
						src={mapMarker}
						alt="marker"
						width="32"
					/>
				</div>
				<GoogleMap
					mapContainerStyle={{ width, height }}
					center={center}
					zoom={zoom}
					onLoad={onMapLoad}
					onDragEnd={onDragEnd}
					options={{
						clickableIcons: false,
						draggable: !viewOnly, // Enable or disable dragging
					}}
				/>
				<div>
					<Typography.Paragraph style={{ color: '#bababa', margin: '10px 0' }}>
						Latitude: {lat}, Longitude: {lng}
					</Typography.Paragraph>
				</div>
			</div>
		</FleetModal>
	);
};

export default MapModal;

export const useMapModal = () => {
	return useState<MapModalState>(null);
};
